import type { Ref } from 'react';
import type { CardDefinition, CardInstance } from '../../core/types';

interface CardViewProps {
  definition: CardDefinition;
  instance?: CardInstance;
  disabled?: boolean;
  selected?: boolean;
  choice?: boolean;
  contextLabel?: string;
  disabledMessage?: string;
  displayOnly?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  upgradePreview?: boolean;
  onClick?: () => void;
}

function rarityLabel(rarity: CardDefinition['rarity']): string {
  return rarity === 'starter' ? 'Forged' : rarity;
}

function disabledReason(definition: CardDefinition, instance?: CardInstance): string | undefined {
  if (definition.unplayable) return 'Unplayable — this card clogs your hand.';
  if (definition.gear && (instance?.durability ?? 0) <= 0) return 'Broken — repair this Gear to play it.';
  return undefined;
}

export function CardView({
  definition,
  instance,
  disabled = false,
  selected,
  choice = false,
  contextLabel,
  disabledMessage,
  displayOnly = false,
  buttonRef,
  upgradePreview = false,
  onClick,
}: CardViewProps) {
  const durability = definition.gear && instance
    ? `${instance.durability ?? 0}/${definition.gear.maxDurability}`
    : null;
  const reason = disabledMessage ?? disabledReason(definition, instance);
  const upgrade = upgradePreview ? definition.upgrade : undefined;
  const upgradeText = upgrade
    ? `${upgrade.cost ?? definition.cost} Energy. ${upgrade.description ?? definition.description}`
    : undefined;
  const label = [
    definition.name,
    `${definition.cost} Energy`,
    definition.description,
    durability ? `${durability} Durability` : '',
    contextLabel ?? '',
    upgradeText ? `After upgrade: ${upgradeText}` : '',
    disabled ? (reason ?? 'Not currently playable') : '',
  ].filter(Boolean).join('. ');

  const className = `game-card game-card--${definition.type} game-card--${definition.rarity}${selected ? ' is-selected' : ''}${choice ? ' is-choice' : ''}${displayOnly ? ' game-card--display' : ''}`;
  const contents = (
    <>
      <span className="game-card__topline">
        <span className="game-card__cost" aria-hidden="true">{definition.cost}</span>
        <span className="game-card__rarity">{rarityLabel(definition.rarity)}</span>
      </span>
      <span className="game-card__art" aria-hidden="true">
        <img data-art-id={definition.id} src={`/art/cathedris/cards/${definition.id}.webp`} alt="" decoding="async" />
      </span>
      <strong className="game-card__name">{definition.name}</strong>
      <span className="game-card__type">{definition.type}</span>
      <span className="game-card__description">{definition.description}</span>
      {upgradeText ? (
        <span className="game-card__upgrade"><small>After upgrade</small><span>{upgradeText}</span></span>
      ) : null}
      <span className="game-card__tags">
        {contextLabel ? <span className="game-card__context">{contextLabel}</span> : null}
        {definition.gear ? (
          <span className={`durability${(instance?.durability ?? 1) === 0 ? ' is-broken' : ''}`}>
            Gear {durability ?? definition.gear.maxDurability}
          </span>
        ) : null}
        {definition.exhaust ? <span>Exhaust</span> : null}
        {definition.ethereal ? <span>Ethereal</span> : null}
        {definition.unplayable ? <span>Unplayable</span> : null}
        {definition.upgrade ? <span>Upgradable</span> : null}
      </span>
    </>
  );

  if (displayOnly) {
    return (
      <article className={className} aria-label={label}>
        {contents}
      </article>
    );
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      className={className}
      disabled={disabled}
      aria-label={label}
      aria-pressed={selected === undefined ? undefined : selected}
      title={reason}
      onClick={onClick}
    >
      {contents}
    </button>
  );
}
